import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Toast, ToastType } from '@/components/ui/Toast'
import { ListPageSkeleton } from '@/components/ui'
import { useTranslation } from 'react-i18next'
import {
  ShoppingCart,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { apiService } from '../../services/api'
import { MarketplaceIntegrationLogo } from './components/MarketplaceIntegrationLogo'

interface Publication {
  id: string
  vehicle: {
    id: string
    brand: string
    model: string
    modelYear: number
  }
  platform: string
  status: string
  externalUrl?: string
  errorMessage?: string
  publishedAt?: string
  createdAt: string
  updatedAt: string
}

interface HistoryResponse {
  data: Publication[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export function PublicationsHistoryPage() {
  const { t } = useTranslation()
  const [history, setHistory] = useState<Publication[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [marketplaceFilter, setMarketplaceFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      })

      if (marketplaceFilter) params.append('marketplace', marketplaceFilter)
      if (statusFilter) params.append('status', statusFilter)

      const response = await apiService.get<HistoryResponse>(
        `/integrations/publications/history?${params}`
      )

      setHistory(response.data)
      setPagination(response.pagination)
    } catch (err: any) {
      setToast({
        message: err.message || t('marketplace.history.error'),
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [pagination.page, marketplaceFilter, statusFilter])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PUBLISHED':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'PENDING':
        return <Clock className="w-4 h-4 text-yellow-600" />
      case 'UPDATING':
        return <Clock className="w-4 h-4 text-blue-600" />
      case 'REMOVED':
        return <XCircle className="w-4 h-4 text-gray-600" />
      default:
        return <AlertTriangle className="w-4 h-4 text-orange-600" />
    }
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'primary' | 'info' | 'warning' | 'danger' | 'gray'> = {
      PUBLISHED: 'primary',
      FAILED: 'danger',
      PENDING: 'warning',
      UPDATING: 'info',
      REMOVED: 'gray',
    }

    const statusKey = status.toUpperCase() as keyof typeof t
    const statusLabel = t(`marketplace.history.statusTypes.${statusKey}` as any) || status

    return (
      <Badge variant={variants[status.toUpperCase()] || 'gray'}>
        <span className="mr-1">{getStatusIcon(status)}</span>
        {statusLabel}
      </Badge>
    )
  }

  const getPlatformIcon = (platform: string) => (
    <MarketplaceIntegrationLogo platform={platform} alt={platform} size="md" />
  )

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  if (loading) {
    return <ListPageSkeleton variant="table" rows={15} />
  }

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t('marketplace.history.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('marketplace.history.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <select
                value={marketplaceFilter}
                onChange={(e) => {
                  setMarketplaceFilter(e.target.value)
                  setPagination((prev) => ({ ...prev, page: 1 }))
                }}
                className="px-4 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="">{t('marketplace.history.allMarketplaces')}</option>
                <option value="MERCADO_LIVRE">Mercado Livre</option>
                <option value="OLX">OLX</option>
                <option value="META">Meta</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPagination((prev) => ({ ...prev, page: 1 }))
                }}
                className="px-4 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="">{t('marketplace.history.allStatuses')}</option>
                <option value="PENDING">{t('marketplace.history.statusTypes.PENDING')}</option>
                <option value="PUBLISHED">{t('marketplace.history.statusTypes.PUBLISHED')}</option>
                <option value="FAILED">{t('marketplace.history.statusTypes.FAILED')}</option>
                <option value="UPDATING">{t('marketplace.history.statusTypes.UPDATING')}</option>
                <option value="REMOVED">{t('marketplace.history.statusTypes.REMOVED')}</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* History Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Total: {pagination.total} publicações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3">{t('marketplace.history.vehicle')}</th>
                    <th className="text-left p-3">{t('marketplace.history.marketplace')}</th>
                    <th className="text-left p-3">{t('marketplace.history.status')}</th>
                    <th className="text-left p-3">{t('marketplace.history.createdAt')}</th>
                    <th className="text-left p-3">{t('marketplace.history.updatedAt')}</th>
                    <th className="text-left p-3">{t('marketplace.history.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        {t('marketplace.history.noPublications')}
                      </td>
                    </tr>
                  ) : (
                    history.map((pub) => (
                      <tr key={pub.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3">
                          <div className="font-medium">
                            {pub.vehicle.brand} {pub.vehicle.model}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {pub.vehicle.modelYear}
                          </div>
                        </td>
                        <td className="p-3 align-top">
                          <div className="flex flex-col items-start gap-2">
                            {getPlatformIcon(pub.platform)}
                            <span className="text-sm font-medium text-foreground">{pub.platform}</span>
                          </div>
                        </td>
                        <td className="p-3">{getStatusBadge(pub.status)}</td>
                        <td className="p-3">
                          <div className="text-sm">
                            {formatDate(pub.createdAt)}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            {formatDate(pub.updatedAt)}
                          </div>
                        </td>
                        <td className="p-3">
                          {pub.externalUrl ? (
                            <a
                              href={pub.externalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-sm"
                            >
                              {t('marketplace.history.viewAd')}
                            </a>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  {t('marketplace.history.page')} {pagination.page} {t('marketplace.history.of')} {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-2 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
